import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { saveDocPage } from '../../../services/api';
import { X, FileText, Loader2, AlertCircle } from 'lucide-react';

const MAX_TITLE = 200;

/* ════════════════════════════════════════════════════════════════
   TemplateGalleryBody — Pure React UI (no ReactiveJSON dependency)
   Props: isOpen, directoryId, onClose
   ════════════════════════════════════════════════════════════════ */

export function TemplateGalleryBody({ isOpen, directoryId, onClose, onCreated }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setTitle(''); setError(''); }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    // Le drapeau `field_is_template` est porté AUSSI par les modèles de sites :
    // seul le répertoire racine (posé au presave sur une page rangée, absent
    // d'une page de site) distingue un modèle du wiki. L'API decoupled refusant
    // une valeur vide, « >= 1 » tient lieu de « IS NOT NULL ».
    fetch(
      '/decoupled-api/node/builder_page/collection?display=decoupled&filter[0][f]=field_is_template&filter[0][v]=1&filter[1][f]=field_root_directory&filter[1][v]=1&filter[1][c]=%3E%3D&sort[0][f]=title&sort[0][d]=ASC',
      { credentials: 'include' },
    )
      .then((res) => res.json())
      .then((data) => {
        const items = (data || []).filter(Boolean).map((item) => {
          const d = item.data ?? item;
          return {
            id: d.id,
            title: d.title || 'Sans titre',
            icon: d.icon || '',
            category: d.category || '',
            summary: d.summary || '',
          };
        });
        setTemplates(items);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const createPage = useCallback(async (templateId = null) => {
    if (!directoryId) {
      setError('Impossible de créer la page : espace non identifié. Rechargez la page.');
      return;
    }
    setError('');
    setCreating(true);
    try {
      let summary = '';

      if (templateId) {
        const tplRes = await fetch(
          `/decoupled-api/node/builder_page/${templateId}?display=decoupled`,
          { credentials: 'include' },
        );
        if (tplRes.ok) {
          const tplJson = await tplRes.json();
          const tplData = tplJson?.data ?? tplJson;
          summary = tplData.summary || '';
        }
      }

      const pageData = {
        title: title.trim() || 'Nouvelle page',
        directoryId,
      };
      if (summary) pageData.summary = summary;
      // Le corps du modèle (ses blocs) est recopié par Drupal au presave, à la
      // seule condition qu'on lui dise de quel modèle il s'agit.
      if (templateId) pageData.templateSource = templateId;

      const result = await saveDocPage(pageData);
      const newId = result?.data?.id || result?.id;
      if (newId) {
        onClose();
        if (onCreated) {
          onCreated(newId);
        } else {
          navigate(`/docs/page/${newId}`);
        }
      } else {
        setError('La page a été créée mais son identifiant est introuvable. Rechargez la page.');
      }
    } catch (err) {
      console.error('Create page failed:', err);
      setError(err.message || 'Erreur lors de la création de la page.');
    } finally {
      setCreating(false);
    }
  }, [directoryId, title, onClose, onCreated, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-stone-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="text-lg font-semibold text-stone-900">Créer une page</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="alert alert-danger items-start mb-4">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-5">
            <label className="field-label mb-1.5">
              Titre de la page
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
              placeholder="Nouvelle page…"
              autoFocus
              className="input"
            />
          </div>

          <button
            onClick={() => createPage(null)}
            disabled={creating}
            className="w-full flex items-center gap-3 p-4 mb-4 bg-stone-50 border-2 border-dashed border-stone-300 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-all text-left disabled:opacity-50"
          >
            <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center">
              <FileText size={18} className="text-stone-400" />
            </div>
            <div>
              <span className="font-medium text-stone-900 block text-sm">Page vierge</span>
              <span className="text-xs text-stone-500">Commencer avec une page vide</span>
            </div>
          </button>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-stone-400" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-stone-400 text-sm text-center py-8">Aucun modèle disponible.</p>
          ) : (
            <>
              <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">
                Depuis un modèle
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => createPage(t.id)}
                    disabled={creating}
                    className="flex items-start gap-3 p-4 bg-white border border-stone-200 rounded-xl hover:border-blue-400 hover:shadow-sm transition-all text-left disabled:opacity-50"
                  >
                    <span className="text-xl flex-shrink-0">{t.icon || '📄'}</span>
                    <div className="min-w-0">
                      <span className="font-medium text-stone-900 block text-sm truncate">
                        {t.title}
                      </span>
                      {t.category && (
                        <span className="text-xs text-stone-400">{t.category}</span>
                      )}
                      {t.summary && (
                        <p className="text-xs text-stone-500 line-clamp-2 mt-1">{t.summary}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {creating && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-2xl">
            <div className="flex items-center gap-2 text-stone-600">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm font-medium">Création en cours...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   DocTemplateGallery — ReactiveJSON wrapper (thin shell)
   ════════════════════════════════════════════════════════════════ */

export const DocTemplateGallery = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const isOpen = evaluateTemplateValue({
    valueToEvaluate: props.isOpen,
    globalDataContext,
    templateContext,
  });

  const openPath = props.openPath || 'templateGalleryOpen';

  const directoryId = evaluateTemplateValue({
    valueToEvaluate: props.directoryId,
    globalDataContext,
    templateContext,
  });

  const handleClose = useCallback(() => {
    globalDataContext.updateData(false, openPath);
  }, [globalDataContext, openPath]);

  return (
    <ActionDependant {...props}>
      <TemplateGalleryBody
        isOpen={isOpen}
        directoryId={directoryId}
        onClose={handleClose}
      />
    </ActionDependant>
  );
};
